// The dock's side of the call window: the fixed "Open call window" row in a call card's tray (never a transient
// banner: when the browser blocks the pop-up, this row says so and its button opens the window from that click), and
// the idle "When a call starts" block with the Live coach, Call window and CRM page switches (the CRM page one off
// until this browser turns it on).
import { useEffect, useSyncExternalStore } from 'react';
import { AppWindow, Columns2, Sparkles } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { Link } from '../../lib/router.tsx';
import { type WorkspaceValue, useWorkspace } from '../../state/workspace.tsx';
import { LiveCoachSwitch } from '../../call/coach/CoachPanel.tsx';
import { CallRecordingSwitch } from './CallRecordingSwitch.tsx';
import { coachUserKey } from '../../call/coach/coach.ts';
import { AUTO_POP_DEFAULT, autoPopPreference } from './contextCard.ts';
import {
  type CallWindowLinkSnapshot, callWindowAccount, callWindowLink, callWindowSupported, callWindowUnsupportedText,
} from '../callWindow/callWindowLaunch.ts';
import { callWindowPreference, useCallWindowEnabled } from '../callWindow/callWindowPrefs.ts';

export function useCallWindowLink(): CallWindowLinkSnapshot {
  return useSyncExternalStore(callWindowLink.subscribe, callWindowLink.getSnapshot, callWindowLink.getSnapshot);
}

type WsLike = Pick<WorkspaceValue, 'session' | 'userId' | 'phoneOwnership'>;

export const callWindowAccountOf = (ws: Pick<WorkspaceValue, 'session' | 'userId'>): string => callWindowAccount(ws.session.tenant?.id, ws.userId);
export const callWindowUserKeyOf = (ws: Pick<WorkspaceValue, 'session' | 'userId'>): string => coachUserKey(ws.session.tenant?.id, ws.userId);

/** Whether a Dial or Answer opens the call window by itself: inside a user gesture, the preference on, this tab owning
 * the phone, and not while viewing as someone else. Read at click time, never from a stale closure. */
export function callWindowAutoOpen(ws: WsLike, gesture: boolean): boolean {
  if (!gesture || ws.session.view_as) return false;
  if (ws.phoneOwnership && ws.phoneOwnership.status !== 'owner') return false;
  return callWindowPreference.get(callWindowUserKeyOf(ws));
}

/** Whether a call window that is already open follows this call: the same rules as auto-open, minus the gesture
 * (telling an open window what to show needs no click). A calling session's auto-dials rely on it. */
export function callWindowFollows(ws: WsLike): boolean {
  return callWindowAutoOpen(ws, true);
}

/** Answer from any of the four places (card, call bar, Alt+Shift+A): opens the window at the call, inside the gesture. */
export function armCallWindowAnswer(ws: WsLike, callId: string): void {
  callWindowLink.armAnswer({ account: callWindowAccountOf(ws), callId, open: callWindowAutoOpen(ws, true), follow: callWindowFollows(ws) });
}

export function callWindowRowText(link: CallWindowLinkSnapshot, callId: string | null, supported: boolean): string {
  if (!supported || link.status === 'unsupported') return callWindowUnsupportedText();
  const mine = callId ? link.callId === callId : !!link.launch;
  if (!mine) return 'Shows the CRM and coach for this call';
  switch (link.status) {
    case 'opened': return 'Open in its own window';
    case 'blocked': return 'Blocked by the browser, click to open';
    case 'off': return 'Off, click to open for this call';
    case 'closed': return 'Closed, click to open again';
    default: return 'Shows the CRM and coach for this call';
  }
}

/** The call's own page, which carries the coach where no call window can open (the desktop app, iPhone). */
export const callPagePath = (callId: string) => `/inbox/calls/${encodeURIComponent(callId)}`;

export type CallWindowDockRowModel =
  | { kind: 'window'; label: string; status: string }
  | { kind: 'page'; label: string; status: string; href: string | null };

/** What the row offers: the call window where it can open, otherwise the call's page (the coach and transcript are
 * there), so a desktop-app or iPhone rep still has the coach one click away. */
export function callWindowDockRowModel(link: CallWindowLinkSnapshot, callId: string | null, supported: boolean): CallWindowDockRowModel {
  if (supported && link.status !== 'unsupported') return { kind: 'window', label: 'Open call window', status: callWindowRowText(link, callId, true) };
  return { kind: 'page', label: 'Open coach on the call’s page', status: `Call window ${callWindowUnsupportedText().replace(/^Not/, 'not')}`, href: callId ? callPagePath(callId) : null };
}

/** Always mounted in the tray: one button and one status line, the same size whatever happened. */
export function CallWindowDockRow({ callId }: { callId: string | null }) {
  const ws = useWorkspace();
  const link = useCallWindowLink();
  // Rendered on the server only by tests, where the browser's own path is the one to check.
  const supported = typeof window === 'undefined' || callWindowSupported();
  useEffect(() => {
    const t = setInterval(() => callWindowLink.refreshClosed(), 1000);
    return () => clearInterval(t);
  }, []);
  const statusId = `call-window-status-${callId ?? 'starting'}`;
  const model = callWindowDockRowModel(link, callId, supported);
  return (
    <div className="call-window-row" data-slot="call-window">
      {model.kind === 'window' ? (
        <button type="button" className="btn btn-block" disabled={!!ws.session.view_as} aria-describedby={statusId}
          onClick={() => callWindowLink.openNow({ account: callWindowAccountOf(ws), callId })}>
          <Icon icon={Columns2} /> {model.label}
        </button>
      ) : model.href ? (
        <Link to={model.href} className="btn btn-block" aria-describedby={statusId}>
          <Icon icon={Sparkles} /> {model.label}
        </Link>
      ) : (
        <button type="button" className="btn btn-block" disabled aria-describedby={statusId}>
          <Icon icon={Sparkles} /> {model.label}
        </button>
      )}
      <p className="field-hint call-window-status" id={statusId} role="status">{model.status}</p>
    </div>
  );
}

function SwitchRow({ id, icon, label, on, available, state, detail, onToggle }: {
  id: string; icon: typeof Columns2; label: string; on: boolean; available: boolean; state: string; detail: string; onToggle: () => void;
}) {
  const tone = available && on ? 'live' : available ? 'neutral' : 'warn';
  return (
    <div className="coach-switch">
      <button type="button" role="switch" aria-checked={available && on} disabled={!available} className="coach-switch-btn" aria-describedby={`${id}-detail`} onClick={onToggle}>
        <Icon icon={icon} /> {label}
        <span className={`chip chip-lamp tone-${tone}`}><span className={`dot dot-${tone}`} aria-hidden="true" />{state}</span>
      </button>
      <p className="field-hint" id={`${id}-detail`}>{detail}</p>
    </div>
  );
}

export function CallWindowSwitch() {
  const ws = useWorkspace();
  const [on, setOn] = useCallWindowEnabled(callWindowUserKeyOf(ws));
  const supported = typeof window === 'undefined' || callWindowSupported();
  return (
    <SwitchRow
      id="call-window-switch" icon={Columns2} label="Call window" on={on} available={supported}
      state={supported ? (on ? 'On' : 'Off') : 'Not available'}
      detail={supported ? 'Opens the CRM and the coach side by side when you dial or answer.' : `${callWindowUnsupportedText()}. During a call, the coach is on the call’s page.`}
      onToggle={() => setOn(!on)}
    />
  );
}

/** Off unless this browser turned it on: the call window already shows the CRM card. One line of copy for both states,
 * so turning it on or off never changes the row's height. */
export function crmPageSwitchDetail(host: string): string {
  return `The call window shows the CRM card. Turn on to also open the CRM’s own page; allow pop-ups for ${host}.`;
}

export function CrmPageSwitch() {
  const on = useSyncExternalStore(autoPopPreference.subscribe, autoPopPreference.get, () => AUTO_POP_DEFAULT);
  const host = typeof window !== 'undefined' ? window.location?.hostname || 'sidet.one' : 'sidet.one';
  return (
    <SwitchRow
      id="crm-page-switch" icon={AppWindow} label="CRM page" on={on} available state={on ? 'On' : 'Off'}
      detail={crmPageSwitchDetail(host)}
      onToggle={() => autoPopPreference.set(!on)}
    />
  );
}

/** Idle only, beside the Dialer: during a call the dock is the call card alone, so nothing here can move it. */
export function CallStartOptions() {
  return (
    <fieldset className="call-start-options">
      <legend className="call-start-title">When a call starts</legend>
      <LiveCoachSwitch />
      <CallRecordingSwitch />
      <CallWindowSwitch />
      <CrmPageSwitch />
    </fieldset>
  );
}
